'use client';

import { useState, useEffect } from 'react';
import { getSupabase } from '@/lib/supabase';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import type { User } from '@supabase/supabase-js';

interface Question {
  id: string;
  page_slug?: string;
  user_name: string | null;
  user_avatar: string | null;
  user_id: string;
  title: string;
  body: string | null;
  created_at: string;
  vote_count: number;
  user_voted: boolean;
  answers: Answer[];
}

interface Answer {
  id: string;
  user_name: string | null;
  user_avatar: string | null;
  user_id: string;
  body: string;
  is_accepted: boolean;
  created_at: string;
}

async function hydrateQuestions(
  sb: ReturnType<typeof getSupabase>,
  qs: { id: string; page_slug?: string; user_name: string | null; user_avatar: string | null; user_id: string; title: string; body: string | null; created_at: string }[],
  user: User | null,
): Promise<Question[]> {
  if (!sb || qs.length === 0) return [];

  const questionIds = qs.map((q) => q.id);

  const [{ data: votes }, { data: answers }, { data: userVotes }] = await Promise.all([
    sb.from('faq_votes').select('question_id').in('question_id', questionIds),
    sb.from('faq_answers')
      .select('id, question_id, user_name, user_avatar, user_id, body, is_accepted, created_at')
      .in('question_id', questionIds)
      .order('created_at', { ascending: true }),
    user
      ? sb.from('faq_votes').select('question_id').in('question_id', questionIds).eq('user_id', user.id)
      : Promise.resolve({ data: [] as { question_id: string }[] }),
  ]);

  const voteCounts = new Map<string, number>();
  votes?.forEach((v) => voteCounts.set(v.question_id, (voteCounts.get(v.question_id) ?? 0) + 1));

  const userVoteSet = new Set(userVotes?.map((v) => v.question_id));

  const answerMap = new Map<string, Answer[]>();
  answers?.forEach((a) => {
    const list = answerMap.get(a.question_id) ?? [];
    list.push(a);
    answerMap.set(a.question_id, list);
  });

  return qs.map((q) => ({
    ...q,
    vote_count: voteCounts.get(q.id) ?? 0,
    user_voted: userVoteSet.has(q.id),
    answers: answerMap.get(q.id) ?? [],
  }));
}

export function FaqTab({ pageSlug, user }: { pageSlug: string; user: User | null }) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [globalQuestions, setGlobalQuestions] = useState<Question[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [answerSubmitting, setAnswerSubmitting] = useState(false);
  const lang = useCalendarStore((s) => s.lang);

  const loadQuestions = async () => {
    const sb = getSupabase();
    if (!sb) return;
    const { data: qs } = await sb
      .from('faq_questions')
      .select('id, user_name, user_avatar, user_id, title, body, created_at')
      .eq('page_slug', pageSlug)
      .eq('is_hidden', false)
      .order('created_at', { ascending: false })
      .limit(50);

    if (!qs) return;

    const hydrated = await hydrateQuestions(sb, qs, user);
    setQuestions(hydrated);

    // If no local questions, load popular questions globally
    if (qs.length === 0) {
      const { data: globalQs } = await sb
        .from('faq_questions')
        .select('id, page_slug, user_name, user_avatar, user_id, title, body, created_at')
        .eq('is_hidden', false)
        .neq('page_slug', pageSlug)
        .order('created_at', { ascending: false })
        .limit(10);

      if (globalQs && globalQs.length > 0) {
        const globalHydrated = await hydrateQuestions(sb, globalQs, user);
        // Sort by vote count descending
        globalHydrated.sort((a, b) => b.vote_count - a.vote_count);
        setGlobalQuestions(globalHydrated);
      }
    } else {
      setGlobalQuestions([]);
    }
  };

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSlug, user?.id]);

  const handleAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !title.trim()) return;

    setSubmitting(true);
    const sb = getSupabase();
    if (!sb) return;
    const { error } = await sb.from('faq_questions').insert({
      page_slug: pageSlug,
      user_id: user.id,
      user_name: user.user_metadata?.full_name ?? user.email,
      user_avatar: user.user_metadata?.avatar_url,
      title: title.trim(),
      body: body.trim() || null,
    });

    if (!error) {
      setTitle('');
      setBody('');
      await loadQuestions();
    }
    setSubmitting(false);
  };

  const toggleVote = async (questionId: string, voted: boolean) => {
    if (!user) return;

    const sb = getSupabase();
    if (!sb) return;
    if (voted) {
      await sb
        .from('faq_votes')
        .delete()
        .eq('question_id', questionId)
        .eq('user_id', user.id);
    } else {
      await sb
        .from('faq_votes')
        .insert({ question_id: questionId, user_id: user.id });
    }

    const updateList = (prev: Question[]) =>
      prev.map((q) =>
        q.id === questionId
          ? { ...q, vote_count: q.vote_count + (voted ? -1 : 1), user_voted: !voted }
          : q
      );
    setQuestions(updateList);
    setGlobalQuestions(updateList);
  };

  const submitAnswer = async (questionId: string) => {
    if (!user || !answerText.trim()) return;

    setAnswerSubmitting(true);
    const sb = getSupabase();
    if (!sb) return;
    const { data, error } = await sb
      .from('faq_answers')
      .insert({
        question_id: questionId,
        user_id: user.id,
        user_name: user.user_metadata?.full_name ?? user.email,
        user_avatar: user.user_metadata?.avatar_url,
        body: answerText.trim(),
      })
      .select()
      .single();

    if (!error && data) {
      const updateList = (prev: Question[]) =>
        prev.map((q) =>
          q.id === questionId ? { ...q, answers: [...q.answers, data] } : q
        );
      setQuestions(updateList);
      setGlobalQuestions(updateList);
      setAnswerText('');
    }
    setAnswerSubmitting(false);
  };

  const acceptAnswer = async (questionId: string, answerId: string) => {
    const sb = getSupabase();
    if (!sb) return;
    await sb
      .from('faq_answers')
      .update({ is_accepted: true })
      .eq('id', answerId);

    const updateList = (prev: Question[]) =>
      prev.map((q) =>
        q.id === questionId
          ? {
              ...q,
              answers: q.answers.map((a) =>
                a.id === answerId ? { ...a, is_accepted: true } : a
              ),
            }
          : q
      );
    setQuestions(updateList);
    setGlobalQuestions(updateList);
  };

  const renderQuestion = (q: Question, showSource: boolean) => (
    <div key={q.id} className="content-card p-3">
      <div className="flex gap-3">
        {/* Vote button */}
        <div className="flex flex-col items-center gap-0.5">
          <button
            onClick={() => toggleVote(q.id, q.user_voted)}
            disabled={!user}
            className={`text-lg transition-colors ${q.user_voted ? 'text-brand' : 'text-muted hover:text-text-secondary'} disabled:opacity-50`}
            aria-label={q.user_voted ? uiFb('ui.remove_vote', 'Remove vote', lang) : uiFb('ui.upvote', 'Upvote', lang)}
          >
            {q.user_voted ? '\u25B2' : '\u25B3'}
          </button>
          <span className="text-xs font-medium text-text-secondary">{q.vote_count}</span>
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <button
            onClick={() => setExpanded(expanded === q.id ? null : q.id)}
            className="text-left text-sm font-medium text-text-primary hover:text-brand"
          >
            {q.title}
          </button>
          <div className="mt-0.5 flex items-center gap-2 text-label text-muted">
            {q.user_avatar && (
              <img src={q.user_avatar} alt="" className="h-4 w-4 rounded-full" />
            )}
            <span>{q.user_name ?? uiFb('ui.anonymous', 'Anonymous', lang)}</span>
            <span>{new Date(q.created_at).toLocaleDateString()}</span>
            <span>{q.answers.length} {q.answers.length === 1 ? uiFb('ui.answer', 'answer', lang) : uiFb('ui.answers', 'answers', lang)}</span>
            {showSource && q.page_slug && (
              <span className="truncate text-brand/70">/{q.page_slug}</span>
            )}
          </div>

          {/* Expanded: body + answers */}
          {expanded === q.id && (
            <div className="mt-3 flex flex-col gap-3">
              {q.body && (
                <p className="text-sm text-text-secondary">{q.body}</p>
              )}

              {/* Answers */}
              {q.answers.map((a) => (
                <div
                  key={a.id}
                  className={`ml-2 rounded-lg border p-2.5 ${a.is_accepted ? 'border-semantic-success/30 bg-semantic-success/5' : 'border-border-subtle bg-surface-elevated'}`}
                >
                  <div className="flex items-center gap-2 text-label text-muted">
                    {a.user_avatar && (
                      <img src={a.user_avatar} alt="" className="h-4 w-4 rounded-full" />
                    )}
                    <span>{a.user_name ?? uiFb('ui.anonymous', 'Anonymous', lang)}</span>
                    {a.is_accepted && (
                      <span className="rounded bg-semantic-success/20 px-1.5 py-0.5 text-caption font-medium text-semantic-success">
                        {uiFb('ui.accepted', 'Accepted', lang)}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-text-secondary">{a.body}</p>
                  {user && user.id === q.user_id && !a.is_accepted && (
                    <button
                      onClick={() => acceptAnswer(q.id, a.id)}
                      className="mt-1 text-label text-muted hover:text-green-600"
                    >
                      {uiFb('ui.accept_answer', 'Accept answer', lang)}
                    </button>
                  )}
                </div>
              ))}

              {/* Answer form */}
              {user && (
                <div className="ml-2 flex gap-2">
                  <input
                    type="text"
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder={uiFb('ui.write_answer', 'Write an answer...', lang)}
                    className="min-w-0 flex-1 content-card px-3 py-1.5 text-sm text-text-primary placeholder:text-muted focus:border-brand focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submitAnswer(q.id);
                      }
                    }}
                  />
                  <button
                    onClick={() => submitAnswer(q.id)}
                    disabled={!answerText.trim() || answerSubmitting}
                    className="rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-text-on-brand transition-opacity disabled:opacity-50"
                  >
                    {uiFb('ui.reply', 'Reply', lang)}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Ask form */}
      {user ? (
        <form onSubmit={handleAsk} className="flex flex-col gap-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={uiFb('ui.ask_question', 'Ask a question...', lang)}
            className="content-card px-3 py-2 text-sm text-text-primary placeholder:text-muted focus:border-brand focus:outline-none"
          />
          {title.trim() && (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={uiFb('ui.add_details', 'Add details (optional)', lang)}
              rows={2}
              className="content-card px-3 py-2 text-sm text-text-primary placeholder:text-muted focus:border-brand focus:outline-none"
            />
          )}
          <button
            type="submit"
            disabled={!title.trim() || submitting}
            className="self-end rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-text-on-brand transition-opacity disabled:opacity-50"
          >
            {submitting ? uiFb('ui.posting', 'Posting...', lang) : uiFb('ui.ask', 'Ask', lang)}
          </button>
        </form>
      ) : (
        <p className="text-sm text-muted">{uiFb('ui.sign_in_qa', 'Sign in to ask or answer questions.', lang)}</p>
      )}

      {/* Questions list */}
      <div className="flex flex-col gap-3">
        {questions.map((q) => renderQuestion(q, false))}
        {questions.length === 0 && globalQuestions.length === 0 && (
          <p className="py-4 text-center text-sm text-muted">{uiFb('ui.no_questions_yet', 'No questions yet. Ask the first one!', lang)}</p>
        )}
      </div>

      {/* Global fallback — popular questions from other pages */}
      {questions.length === 0 && globalQuestions.length > 0 && (
        <div className="flex flex-col gap-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted">
            {uiFb('ui.popular_questions', 'Popular questions across the site', lang)}
          </p>
          {globalQuestions.map((q) => renderQuestion(q, true))}
        </div>
      )}
    </div>
  );
}
